import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FloatingField,
  LabeledInput,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { Grid } from '@/common/components/Grid/Grid';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeCardNumberElementOptions,
} from '@stripe/stripe-js';
import {
  TCardFieldInfo,
  TCardFieldsInfo,
  TCreditCardFieldProps,
  TCreditCardFieldsProps,
} from './CreditCardFields.types';

export function CreditCardFields({ info, onChange }: TCreditCardFieldsProps) {
  const handleChange =
    (field: keyof TCardFieldsInfo) => (evt: TCardFieldInfo) => {
      onChange({
        ...info,
        [field]: evt,
      });
    };

  return (
    <>
      <CardNumberField info={info.number} onChange={handleChange('number')} />
      <Grid columnBasis="basis-[15rem]" gap="md">
        <CardExpiryField info={info.expiry} onChange={handleChange('expiry')} />
        <CardCvcField info={info.cvc} onChange={handleChange('cvc')} />
      </Grid>
    </>
  );
}

export function CardNumberField({
  className,
  info,
  onChange,
}: TCreditCardFieldProps) {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);
  const [element, setElement] = useState<StripeCardNumberElement | null>(null);
  return (
    <FloatingField
      focused={focused}
      error={t(info.error)}
      onPress={() => element?.focus()}
      className={className}
    >
      <Stack horizontal grow align="center">
        <LabeledInput
          label={t('credit_card_form.fields.card_number')}
          inputProps={{
            id: 'cardNumber',
          }}
          active={focused || !info.empty}
          customElement={
            <CardNumberElement
              id="cardNumber"
              onFocus={() => {
                setFocused(true);
              }}
              onReady={(element) => setElement(element)}
              onBlur={() => setFocused(false)}
              onChange={(evt) => {
                onChange({
                  empty: evt.empty,
                  complete: evt.complete,
                  error: evt.error?.code
                    ? `stripe.errors.${evt.error.code}`
                    : '',
                  brand: evt.brand,
                });
              }}
              options={elementsOptions}
            />
          }
        />
        {info.error ? <Icon icon="circle-xmark" color="systemError" /> : null}
      </Stack>
    </FloatingField>
  );
}

export const CardExpiryField = ({
  className,
  info,
  onChange,
}: TCreditCardFieldProps) => {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);
  const [element, setElement] = useState<StripeCardExpiryElement | null>(null);

  return (
    <FloatingField
      focused={focused}
      error={t(info.error)}
      onPress={() => element?.focus()}
      className={className}
    >
      <LabeledInput
        label={t('credit_card_form.fields.card_expiry')}
        inputProps={{
          id: 'cardExpiry',
        }}
        active={focused || !info.empty}
        customElement={
          <CardExpiryElement
            id="cardExpiry"
            onReady={(element) => setElement(element)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(evt) => {
              onChange({
                empty: evt.empty,
                complete: evt.complete,
                error: evt.error?.code ? `stripe.errors.${evt.error.code}` : '',
              });
            }}
            options={elementsOptions}
          />
        }
      />
      {info.error ? <Icon icon="circle-xmark" color="systemError" /> : null}
    </FloatingField>
  );
};

export const CardCvcField = ({
  className,
  info,
  onChange,
}: TCreditCardFieldProps) => {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);
  const [element, setElement] = useState<StripeCardCvcElement | null>(null);

  return (
    <FloatingField
      focused={focused}
      error={t(info.error)}
      onPress={() => element?.focus()}
      className={className}
    >
      <LabeledInput
        label={t('credit_card_form.fields.card_cvc')}
        inputProps={{
          id: 'cardCVC',
        }}
        active={focused || !info.empty}
        customElement={
          <CardCvcElement
            id="cardCVC"
            onReady={(element) => setElement(element)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(evt) => {
              onChange({
                empty: evt.empty,
                complete: evt.complete,
                error: evt.error?.code ? `stripe.errors.${evt.error.code}` : '',
              });
            }}
            options={elementsOptions}
          />
        }
      />
      {info.error ? <Icon icon="circle-xmark" color="systemError" /> : null}
    </FloatingField>
  );
};

const elementsOptions: StripeCardNumberElementOptions = {
  placeholder: '',
  style: {
    base: {
      color: '#333E4A',
      fontWeight: 'bold',
      fontSize: '16px',
    },
    invalid: {
      color: '#333E4A',
    },
  },
  disableLink: true,
};
